// SPDX-License-Identifier: AGPL-3.0-or-later

import Authentication from '@app/features/auth/state/Authentication';
import {
	formatGamePlayDuration,
	gameActivityToKey,
	sanitizeGameIconDataUrl,
	type GameActivityPresence,
} from '@app/features/game_activity/GameActivity';
import {GameActivityEmitter} from '@app/features/game_activity/GameActivityEmitter';
import {trackGameSession} from '@app/features/game_activity/GameHistoryReporter';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {getElectronAPI, isElectron} from '@app/features/ui/utils/NativeUtils';
import type {GameActivityConfig, GameActivityPayload} from '@app/types/electron.d';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('GameActivityStore');

interface GameActivityElectronApi {
	getCurrentGameActivity(): Promise<GameActivityPayload | null>;
	onGameActivityChanged(callback: (activity: GameActivityPayload | null) => void): () => void;
	getGameActivityConfig(): Promise<GameActivityConfig | null>;
	setGameActivityEnabled(enabled: boolean): Promise<GameActivityConfig | null>;
}

function payloadToPresence(activity: GameActivityPayload | null, startedAtMs: number | null): GameActivityPresence | null {
	if (!activity?.steamAppId) {
		return null;
	}
	return {
		name: activity.name,
		startedAtMs,
		steamAppId: activity.steamAppId,
		steamIconHash: activity.steamIconHash ?? null,
		iconDataUrl: sanitizeGameIconDataUrl(activity.iconDataUrl),
	};
}

class GameActivityStoreImpl {
	activeGame: GameActivityPayload | null = null;
	enabled = true;
	playedSince: number | null = null;
	private started = false;
	private unsubscribe: (() => void) | null = null;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	get presenceActivity(): GameActivityPresence | null {
		if (!this.enabled) {
			return null;
		}
		return payloadToPresence(this.activeGame, this.playedSince);
	}

	getPresenceActivity(): GameActivityPresence | null {
		return this.presenceActivity;
	}

	get playDurationLabel(): string {
		if (!this.playedSince) {
			return '';
		}
		return formatGamePlayDuration(Date.now() - this.playedSince);
	}

	private notifyPresenceChanged(): void {
		const userId = Authentication.currentUserId;
		if (userId) {
			GameActivityEmitter.emitPresenceChange(userId);
		}
	}

	private setActiveGame(activity: GameActivityPayload | null): void {
		const previousKey = gameActivityToKey(this.presenceActivity);
		const previousId = this.activeGame?.id ?? null;
		const nextId = activity?.id ?? null;
		this.activeGame = activity;
		if (nextId !== previousId) {
			this.playedSince = nextId ? Date.now() : null;
		}
		trackGameSession(this.presenceActivity);
		if (gameActivityToKey(this.presenceActivity) === previousKey) {
			return;
		}
		this.notifyPresenceChanged();
	}

	private getGameActivityApi(): GameActivityElectronApi | null {
		const api = getElectronAPI();
		if (
			!api?.getCurrentGameActivity ||
			!api.onGameActivityChanged ||
			!api.getGameActivityConfig ||
			!api.setGameActivityEnabled
		) {
			return null;
		}
		return {
			getCurrentGameActivity: api.getCurrentGameActivity,
			onGameActivityChanged: api.onGameActivityChanged,
			getGameActivityConfig: api.getGameActivityConfig,
			setGameActivityEnabled: api.setGameActivityEnabled,
		};
	}

	async loadConfig(): Promise<void> {
		const api = this.getGameActivityApi();
		if (!api) {
			return;
		}
		try {
			const config = await api.getGameActivityConfig();
			runInAction(() => {
				const previousKey = gameActivityToKey(this.presenceActivity);
				this.enabled = config?.enabled !== false;
				if (gameActivityToKey(this.presenceActivity) !== previousKey) {
					this.notifyPresenceChanged();
				}
			});
		} catch (error) {
			logger.warn('Failed to load game activity config', error);
		}
	}

	async setEnabled(enabled: boolean): Promise<void> {
		const api = this.getGameActivityApi();
		if (!api) {
			return;
		}
		try {
			const config = await api.setGameActivityEnabled(enabled);
			let current: GameActivityPayload | null = null;
			if (enabled) {
				current = await api.getCurrentGameActivity();
			}
			runInAction(() => {
				this.enabled = config?.enabled !== false;
				this.setActiveGame(this.enabled ? current : null);
			});
		} catch (error) {
			logger.warn('Failed to set game activity enabled', error);
			await this.loadConfig();
		}
	}

	async start(): Promise<void> {
		if (!isElectron() || this.started) {
			return;
		}
		const api = this.getGameActivityApi();
		if (!api) {
			return;
		}
		this.started = true;
		await this.loadConfig();
		try {
			const current = await api.getCurrentGameActivity();
			runInAction(() => {
				this.setActiveGame(this.enabled ? current : null);
			});
		} catch (error) {
			logger.warn('Failed to read initial game activity', error);
		}
		this.unsubscribe = api.onGameActivityChanged((activity) => {
			runInAction(() => {
				this.setActiveGame(this.enabled ? activity : null);
			});
		});
	}
}

const GameActivityStore = new GameActivityStoreImpl();

if (isElectron()) {
	void GameActivityStore.start();
}

export default GameActivityStore;
